import { Currency, Token } from '@etcswapv2/sdk-legacy'
import React, { useMemo } from 'react'
import styled from 'styled-components'

import EthereumLogo from '../../assets/images/ethereum-classic-logo.png'
import useHttpLocations from '../../hooks/useHttpLocations'
import { useAllTokens } from '../../hooks/Tokens'
import { WrappedTokenInfo } from '../../state/lists/hooks'
import Logo from '../Logo'

const getTokenLogoURL = (address: string, chainId: number = 61) =>
  `https://raw.githubusercontent.com/etcswap/tokens/main/ethereum-classic/chains/${chainId}/${address}/logo.png`

const StyledEthereumLogo = styled.img<{ $size: string }>`
  width: ${({ $size }) => $size};
  height: ${({ $size }) => $size};
  box-shadow: 0px 6px 10px rgba(0, 0, 0, 0.075);
  border-radius: 24px;
`

const StyledLogo = styled(Logo)<{ $size: string }>`
  width: ${({ $size }) => $size};
  height: ${({ $size }) => $size};
`

export default function CurrencyLogo({
  currency,
  size = '24px',
  style
}: {
  currency?: Currency
  size?: string
  style?: React.CSSProperties
}) {
  const allTokens = useAllTokens()

  // If currency is a plain Token (not WrappedTokenInfo), look it up in token lists
  // This handles the case where Pair objects return plain SDK Tokens without logoURI
  const wrappedTokenInfo = useMemo(() => {
    if (!currency || !('address' in currency)) return undefined
    if (currency instanceof WrappedTokenInfo) return currency

    // Look up in token lists by address (case-insensitive)
    const token = currency as Token
    const fromList = allTokens[token.address]
    if (fromList instanceof WrappedTokenInfo) return fromList

    // Fallback: try lowercase lookup
    const lowerAddress = token.address.toLowerCase()
    const found = Object.values(allTokens).find(
      t => t.address.toLowerCase() === lowerAddress && t instanceof WrappedTokenInfo
    )
    return found as WrappedTokenInfo | undefined
  }, [currency, allTokens])

  const uriLocations = useHttpLocations(wrappedTokenInfo?.logoURI)

  // Use 'address' property check instead of instanceof to avoid module duplication issues
  const srcs: string[] = useMemo(() => {
    // Use 'address' property check instead of reference equality to avoid module duplication issues
    if (!currency || !('address' in currency)) return []

    const token = currency as Token
    if (wrappedTokenInfo) {
      return [...uriLocations, getTokenLogoURL(wrappedTokenInfo.address, token.chainId)]
    }

    return [getTokenLogoURL(token.address, token.chainId)]
  }, [currency, wrappedTokenInfo, uriLocations])

  // Use 'address' property check instead of reference equality to avoid module duplication issues
  if (!currency || !('address' in currency)) {
    return <StyledEthereumLogo src={EthereumLogo} $size={size} style={style} />
  }

  return <StyledLogo $size={size} srcs={srcs} alt={`${currency?.symbol ?? 'token'} logo`} style={style} />
}
